import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import axios from "axios";
import { toast } from "sonner";
import { Users, Trophy, Calendar, Tag, Shield, Zap, Store, Eye, EyeOff, GraduationCap } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const REDIRECT_URL = `${window.location.origin}/dashboard`;

// Branding configurations
const BRANDING_CONFIG = {
  scc: {
    clubName: "Football & More Limited",
    clubFullName: "",
    tagline: "Everything Football and More",
    logoUrl: "/branding/football-and-more-white.png",
    logoAlt: "Football And More Logo",
    primaryColor: "yellow",
    accentClass: "text-yellow-500",
    buttonClass: "bg-yellow-500/100 hover:bg-yellow-400 text-black",
    borderClass: "border-yellow-500",
    hoverBorderClass: "hover:border-yellow-500",
    bgAccentClass: "bg-yellow-500/10",
    showLogo: true,
    membershipLabel: "SCC membership number",
    welcomeTitle: "Welcome to SCC Football",
    isSccBranding: true,
    showMembershipLink: true
  },
  generic: {
    clubName: "Football & More Limited",
    clubFullName: "",
    tagline: "Everything Football and More",
    logoUrl: "/branding/football-and-more-white.png",
    logoAlt: "Football And More",
    // One brand, one palette — the gold is the mark, so generic mode only drops
    // the SCC-specific wording, never the colours.
    primaryColor: "yellow",
    accentClass: "text-yellow-500",
    buttonClass: "bg-yellow-500/100 hover:bg-yellow-400 text-black",
    borderClass: "border-yellow-500",
    hoverBorderClass: "hover:border-yellow-500",
    bgAccentClass: "bg-yellow-500/10",
    showLogo: true,
    membershipLabel: "membership ID",
    welcomeTitle: "Welcome",
    isSccBranding: false,
    showMembershipLink: false
  }
};

export default function LandingPage({ setUser }) {
  const [showAuth, setShowAuth] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState(""); // Keep for backward compatibility (display)
  const [givenName, setGivenName] = useState("");
  const [lastName, setLastName] = useState("");
  const [membershipNumber, setMembershipNumber] = useState("");
  const [phone, setPhone] = useState("");
  const [birthdate, setBirthdate] = useState("");
  const [joinedSccDate, setJoinedSccDate] = useState("");
  const [primaryTeam, setPrimaryTeam] = useState("");
  const [secondTeam, setSecondTeam] = useState("");
  const [sectionType, setSectionType] = useState("football");
  const [paymentProof, setPaymentProof] = useState("");
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Branding state
  const [brandingMode, setBrandingMode] = useState('scc');
  const branding = BRANDING_CONFIG[brandingMode] || BRANDING_CONFIG.scc;
  
  // Multi-step registration states
  const [isSccMember, setIsSccMember] = useState(null); // null, true, false
  const [isFootballSection, setIsFootballSection] = useState(null); // null, true, false
  const [membershipIntent, setMembershipIntent] = useState(null); // 'non-playing', 'playing'
  const [acceptedDisclaimer, setAcceptedDisclaimer] = useState(false);
  const [registrationStep, setRegistrationStep] = useState(1); // 1, 2, 3, 4
  const [userType, setUserType] = useState(null); // 'coach', 'parent' for non-members
  const [membershipCheckResult, setMembershipCheckResult] = useState(null); // null, {exists, masked_email}
  const [checkingMembership, setCheckingMembership] = useState(false);
  const [authTab, setAuthTab] = useState("login"); // "login" or "register"
  
  // Sign-in type state (Section Member or Academy Parent)
  const [signInType, setSignInType] = useState("section"); // "section" or "academy"
  const [showAccountLookup, setShowAccountLookup] = useState(false);
  const [lookupEmail, setLookupEmail] = useState("");
  const [lookupChildReg, setLookupChildReg] = useState("");
  const [lookupResult, setLookupResult] = useState(null);
  const [lookingUp, setLookingUp] = useState(false);
  // Membership validation prompt (Feb 2026 — replaces hard reject of "Invalid membership number")
  const [mnPromptOpen, setMnPromptOpen] = useState(false);
  const [mnPromptMsg, setMnPromptMsg] = useState("");
  
  // Fetch branding settings on load
  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const response = await axios.get(`${API}/public/branding`);
        setBrandingMode(response.data.branding_mode || 'scc');
      } catch (error) {
        // Default to SCC branding if API fails
        console.log("Using default SCC branding");
      }
    };
    fetchBranding();
  }, []);
  
  // Check for URL params to auto-open login with pre-filled email
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const loginParam = urlParams.get('login');
    const emailParam = urlParams.get('email');
    
    if (loginParam === 'true') {
      setShowAuth(true);
      setAuthTab("login");
      if (emailParam) {
        setEmail(decodeURIComponent(emailParam));
      }
      // Clear the URL params after reading
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);
  
  // NEW: Combined registration options for Section Members
  const [registrationOptions, setRegistrationOptions] = useState({
    sectionMember: false,
    academyParent: false,
    coach: false
  });
  const [linkedRegistrationId, setLinkedRegistrationId] = useState(null);
  
  // Coach application states
  const [coachGivenName, setCoachGivenName] = useState("");
  const [coachLastName, setCoachLastName] = useState("");
  const [coachPhone, setCoachPhone] = useState("");
  const [coachEmail, setCoachEmail] = useState("");
  const [coachBirthdate, setCoachBirthdate] = useState("");
  const [nrocRegistered, setNrocRegistered] = useState(false);
  const [coachingCertifications, setCoachingCertifications] = useState([]);
  const [currentlyCoaching, setCurrentlyCoaching] = useState([]);
  
  // Academy Parent registration states
  const [parentAddress, setParentAddress] = useState("");
  const [children, setChildren] = useState([{
    given_name: "",
    last_name: "",
    birthdate: "",
    gender: "",
    email: "",
    emergency_contact: "",
    emergency_relationship: "",
    medical_info: ""
  }]);
  
  // Password visibility states
  const [showPassword, setShowPassword] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  
  // Check if membership number is already registered
  const checkMembershipNumber = async () => {
    if (!membershipNumber || membershipNumber.trim() === "") {
      toast.error(`Please enter your ${branding.membershipLabel}`);
      return false;
    }
    
    setCheckingMembership(true);
    setMembershipCheckResult(null);
    
    try {
      const response = await axios.get(`${API}/auth/check-membership/${membershipNumber.trim()}`);
      
      if (response.data.exists) {
        setMembershipCheckResult(response.data);
        return false; // Don't proceed
      }
      
      return true; // Proceed to next step
    } catch (error) {
      console.error("Error checking membership:", error);
      // Allow proceeding if check fails (graceful degradation)
      return true;
    } finally {
      setCheckingMembership(false);
    }
  };

  // Academy parent account lookup
  const handleAccountLookup = async () => {
    if (!lookupEmail && !lookupChildReg) {
      toast.error("Please enter your email or child's registration number");
      return;
    }
    
    setLookingUp(true);
    setLookupResult(null);
    
    try {
      const response = await axios.post(`${API}/auth/academy-parent-lookup`, {
        email: lookupEmail || null,
        child_registration_number: lookupChildReg || null
      });
      
      setLookupResult(response.data);
      
      if (response.data.found && response.data.has_account) {
        toast.success("Account found! Check below for your login details.");
      } else if (response.data.found && !response.data.has_account) {
        toast.info("Child found, but no account exists yet.");
      } else {
        toast.error("No account found with the provided information.");
      }
    } catch (error) {
      console.error("Account lookup error:", error);
      toast.error(error.response?.data?.detail || "Failed to look up account");
      setLookupResult({ found: false, message: "An error occurred. Please try again." });
    } finally {
      setLookingUp(false);
    }
  };

  // Reset form helper
  const resetForm = () => {
    setEmail("");
    setPassword("");
    setName("");
    setGivenName("");
    setLastName("");
    setMembershipNumber("");
    setPhone("");
    setBirthdate("");
    setJoinedSccDate("");
    setPrimaryTeam("");
    setSecondTeam("");
    setPaymentProof("");
    setIsSccMember(null);
    setIsFootballSection(null);
    setMembershipIntent(null);
    setAcceptedDisclaimer(false);
    setRegistrationStep(1);
    setUserType(null);
    setMembershipCheckResult(null);
    setCheckingMembership(false);
    setRegistrationOptions({ sectionMember: false, academyParent: false, coach: false });
    setLinkedRegistrationId(null);
    setCoachGivenName("");
    setCoachLastName("");
    setCoachPhone("");
    setCoachEmail("");
    setCoachBirthdate("");
    setNrocRegistered(false);
    setCoachingCertifications([]);
    setCurrentlyCoaching([]);
    setParentAddress("");
    // Reset account lookup states
    setShowAccountLookup(false);
    setLookupEmail("");
    setLookupChildReg("");
    setLookupResult(null);
    setLookingUp(false);
    setChildren([{
      given_name: "",
      last_name: "",
      birthdate: "",
      gender: "",
      email: "",
      emergency_contact: "",
      emergency_relationship: "",
      medical_info: ""
    }]);
  };
  
  // Real data for hover previews
  const [stats, setStats] = useState({
    teamsCount: 0,
    upcomingGames: 0,
    recentReports: [],
    upcomingEvents: 0,
    activeDiscounts: 0,
    totalMembers: 0
  });
  const [hoveredCard, setHoveredCard] = useState(null);

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    if (showAuth) {
      fetchTeams();
      // Reset registration form to step 1 when modal opens
      resetForm();
      setAuthTab("login"); // Default to login tab
    }
  }, [showAuth]);
  
  const fetchStats = async () => {
    try {
      // Fetch teams count
      const teamsRes = await axios.get(`${API}/teams`);
      
      // Fetch games count  
      const gamesRes = await axios.get(`${API}/games`).catch(() => ({ data: [] }));
      const now = new Date();
      const upcomingGames = gamesRes.data.filter(g => new Date(g.date) > now && g.status === 'scheduled').length;
      
      // Fetch recent match reports
      const reportsRes = await axios.get(`${API}/match-reports`).catch(() => ({ data: [] }));
      const recentReports = reportsRes.data.slice(0, 3);
      
      // Fetch events count
      const eventsRes = await axios.get(`${API}/events`).catch(() => ({ data: [] }));
      const upcomingEvents = eventsRes.data.filter(e => new Date(e.date) > now).length;
      
      // Fetch discounts count
      const discountsRes = await axios.get(`${API}/discounts/public`).catch(() => ({ data: [] }));
      const activeDiscounts = discountsRes.data.filter(d => d.is_active).length;
      
      // Fetch total members count
      const membersRes = await axios.get(`${API}/members/count`).catch(() => ({ data: { count: 0 } }));
      const totalMembers = membersRes.data.count || 0;
      
      // Fetch academy stats
      const academyStatsRes = await axios.get(`${API}/academy/public-stats`).catch(() => ({ data: { players_count: 0, teams_count: 0 } }));
      const academyPlayers = academyStatsRes.data.players_count || 0;
      const academyTeams = academyStatsRes.data.teams_count || 0;
      
      setStats({
        teamsCount: teamsRes.data.length,
        upcomingGames,
        recentReports,
        upcomingEvents,
        activeDiscounts,
        totalMembers,
        academyPlayers,
        academyTeams
      });
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const fetchTeams = async () => {
    try {
      const response = await axios.get(`${API}/teams`);
      setTeams(response.data);
    } catch (error) {
      console.error("Error fetching teams:", error);
    }
  };

  const handleGoogleLogin = () => {
    const authUrl = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(REDIRECT_URL)}`;
    window.location.href = authUrl;
  };

  const handleEmailRegister = async (e) => {
    e.preventDefault();
    
    // Validation based on registration path
    if (!email || !password || !name) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    // Birthdate is REQUIRED for everyone
    if (!birthdate) {
      toast.error("Please enter your birthdate");
      return;
    }
    
    // SCC Members must provide membership number (REQUIRED for SCC branding)
    if (branding.isSccBranding && isSccMember && !membershipNumber) {
      toast.error(`Please enter your ${branding.membershipLabel} (REQUIRED)`);
      return;
    }
    
    // Non-playing members must upload payment proof
    if (membershipIntent === 'non-playing' && !paymentProof) {
      toast.error("Please upload your PayNow payment proof");
      return;
    }
    
    // Must accept disclaimer for non-section members
    if (isSccMember && !isFootballSection && !acceptedDisclaimer) {
      toast.error("Please read and accept the disclaimer");
      return;
    }

    setLoading(true);
    try {
      // Path 3: Playing membership - send email instead of registering
      if (membershipIntent === 'playing') {
        await axios.post(`${API}/contact/football-section-enquiry`, {
          name,
          email,
          phone,
          membership_number: membershipNumber,
          message: branding.isSccBranding 
            ? `I am an SCC member (non-football section) and would like to sign up for PLAYING membership in the Football section.`
            : `I would like to sign up for a playing membership.`
        });
        
        toast.success("Your request has been sent to the membership department. They will contact you soon!");
        setShowAuth(false);
        resetForm();
        return;
      }
      
      // Path 1 & 2: Standard registration
      // Wrapped so we can retry with membership_proceed_anyway=true when the
      // canonical SCC validator can't verify the number.
      const submitRegistration = async (proceedAnyway = false) => {
        await axios.post(`${API}/auth/register`, {
          email,
          password,
          name,
          given_name: givenName,
          last_name: lastName,
          membership_number: membershipNumber,
          phone,
          birthdate,
          joined_scc_date: joinedSccDate,
          primary_team: primaryTeam,
          second_team: secondTeam,
          section_type: isFootballSection ? "football" : "non-football",
          membership_type: membershipIntent === 'non-playing' ? 'non-section' : 'section',
          payment_proof: paymentProof,
          is_scc_member: isSccMember,
          membership_proceed_anyway: proceedAnyway,
        });
      };

      try {
        await submitRegistration(false);
      } catch (err) {
        const detail = err.response?.data?.detail || "";
        const status = err.response?.status;
        // 400 + message starting with "We couldn't validate this membership number"
        // → membership validation failure. Show the two-option prompt instead
        // of dumping a toast and bailing.
        if (
          status === 400 &&
          isSccMember &&
          typeof detail === "string" &&
          detail.toLowerCase().includes("validate this membership number")
        ) {
          setMnPromptMsg(detail);
          setMnPromptOpen(true);
          return;
        }
        throw err;
      }

      toast.success("Registration successful! Please wait for admin authorization.");
      setShowAuth(false);
      resetForm();
    } catch (error) {
      console.error("Registration error:", error);
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  // Membership-validation prompt handlers (Feb 2026)
  // When the canonical SCC validator can't verify a number, we surface a
  // 2-button modal instead of erroring out. Both buttons retry registration
  // with the same form data but different flags.
  const submitRegistrationWithFlag = async (overrides) => {
    setLoading(true);
    try {
      await axios.post(`${API}/auth/register`, {
        email,
        password,
        name,
        given_name: givenName,
        last_name: lastName,
        membership_number: membershipNumber,
        phone,
        birthdate,
        joined_scc_date: joinedSccDate,
        primary_team: primaryTeam,
        second_team: secondTeam,
        section_type: isFootballSection ? "football" : "non-football",
        membership_type: membershipIntent === 'non-playing' ? 'non-section' : 'section',
        payment_proof: paymentProof,
        is_scc_member: isSccMember,
        membership_proceed_anyway: false,
        ...overrides,
      });
      setMnPromptOpen(false);
      toast.success("Registration submitted. Admin team will verify and authorize your account.");
      setShowAuth(false);
      resetForm();
    } catch (err) {
      const detail = err.response?.data?.detail || "Registration failed";
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const handleProceedAnyway = async () => {
    await submitRegistrationWithFlag({ membership_proceed_anyway: true });
  };

  const handleSwitchToNonMember = async () => {
    await submitRegistrationWithFlag({
      is_scc_member: false,
      membership_number: "",
    });
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );

      if (response.data.user) {
        // Store session token in localStorage for Safari/cookie fallback
        if (response.data.session_token) {
          localStorage.setItem('scc_session_token', response.data.session_token);
        }
        
        setUser(response.data.user);
        
        // Check if user is pending approval
        if (response.data.is_pending) {
          // Redirect to pending approval page where they can complete payment
          window.location.href = "/pending-approval";
        } else if (response.data.user.is_player_account) {
          // Redirect players to their dashboard
          window.location.href = "/player-dashboard";
        } else {
          // Redirect approved users to main dashboard
          window.location.href = "/dashboard";
        }
      }
    } catch (error) {
      console.error("Login error:", error);
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-black">
      {/* Hero Section */}
      <section className="container mx-auto px-4 py-16 md:py-24">
        <div className="text-center mb-12">
          {/* Logo - Conditional based on branding */}
          <div className="flex justify-center mb-8 animate-fade-in-up">
            {branding.showLogo ? (
              <div className={`force-dark bg-black rounded-2xl px-10 py-7 md:px-14 md:py-9 shadow-2xl border-2 ${branding.borderClass}/50`}>
                <img 
                  src={branding.logoUrl}
                  alt={branding.logoAlt}
                  className="h-32 md:h-44 lg:h-52 w-auto"
                  loading="eager"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className={`hidden h-24 md:h-28 lg:h-32 w-24 md:w-28 lg:w-32 items-center justify-center`}>
                  <span className={`${branding.accentClass} text-4xl md:text-5xl font-bold`}>⚽</span>
                </div>
              </div>
            ) : (
              <div className={`force-dark bg-slate-800 rounded-full p-5 md:p-6 shadow-2xl border-2 ${branding.borderClass}/50`}>
                <div className="h-24 md:h-28 lg:h-32 w-24 md:w-28 lg:w-32 flex items-center justify-center">
                  <span className="text-6xl md:text-7xl">⚽</span>
                </div>
              </div>
            )}
          </div>
          
          {/* Premium Heading */}
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold mb-4 tracking-wide animate-fade-in-up animate-delay-100 text-gold-gradient">
            {branding.clubName}
          </h1>
          
          {/* Elegant Subheading - Only show if clubFullName exists */}
          {branding.clubFullName && (
            <p className="text-gray-400 text-lg md:text-xl mb-3 font-light tracking-widest uppercase animate-fade-in-up animate-delay-200">
              {branding.clubFullName}
            </p>
          )}
          <p className="text-gray-500 text-sm md:text-base mb-8 animate-fade-in-up animate-delay-200">
            {branding.tagline}
          </p>
          
          {/* Stats Row */}
          <div className="flex justify-center gap-8 md:gap-16 mb-10 animate-fade-in-up animate-delay-300">
            <div className="text-center">
              <p className={`font-display text-3xl md:text-4xl font-bold ${branding.accentClass}`}>{stats.totalMembers || '230'}+</p>
              <p className="text-gray-500 text-xs md:text-sm uppercase tracking-wider">Members</p>
            </div>
            <div className="text-center border-l border-r border-gray-700 px-8 md:px-16">
              <p className={`font-display text-3xl md:text-4xl font-bold ${branding.accentClass}`}>{stats.teamsCount || '8'}</p>
              <p className="text-gray-500 text-xs md:text-sm uppercase tracking-wider">Teams</p>
            </div>
            <div className="text-center">
              <p className={`font-display text-3xl md:text-4xl font-bold ${branding.accentClass}`}>7</p>
              <p className="text-gray-500 text-xs md:text-sm uppercase tracking-wider">Days a Week</p>
            </div>
          </div>
          
          {/* CTA Button */}
          <Button
            onClick={() => setShowAuth(true)}
            size="lg"
            className={`${branding.buttonClass} font-bold px-10 py-6 text-lg rounded-full shadow-lg transition-all duration-300 animate-fade-in-up animate-delay-400`}
            data-testid="get-started-button"
          >
            Get Started
          </Button>
          
          {/* Scroll Indicator */}
          <div className="mt-16 animate-bounce-slow">
            <p className="text-gray-600 text-sm mb-2">Scroll to explore</p>
            <svg className="w-6 h-6 mx-auto text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </div>
        </div>
      </section>

      {/* Login/Register Dialog */}
      {showAuth && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <Card className="max-w-md w-full p-6 my-8 max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => setShowAuth(false)}
              className="float-right text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
            <h2 className="text-2xl font-bold mb-6 text-center">{branding.welcomeTitle}</h2>

            <Tabs value={authTab} onValueChange={setAuthTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="login">Login</TabsTrigger>
                <TabsTrigger value="register">Register</TabsTrigger>
              </TabsList>

              <TabsContent value="login">
                {/* Sign-in Type Selector */}
                <div className="mb-6">
                  <p className="text-sm text-gray-600 mb-3 text-center">I am a...</p>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setSignInType("section");
                        setShowAccountLookup(false);
                        setLookupResult(null);
                      }}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        signInType === "section"
                          ? "border-yellow-500 bg-yellow-500/10"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <Trophy className="w-6 h-6 mx-auto mb-1 text-yellow-600" />
                      <span className="text-sm font-medium block">Section Member</span>
                      <span className="text-xs text-gray-500">Players & Members</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setSignInType("academy");
                        setShowAccountLookup(false);
                        setLookupResult(null);
                      }}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        signInType === "academy"
                          ? "border-blue-500 bg-blue-500/10"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <GraduationCap className="w-6 h-6 mx-auto mb-1 text-blue-600" />
                      <span className="text-sm font-medium block">Academy Parent</span>
                      <span className="text-xs text-gray-500">Parent/Guardian</span>
                    </button>
                  </div>
                </div>

                {/* Show Account Lookup for Academy Parents */}
                {signInType === "academy" && !showAccountLookup && (
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800 mb-2">
                      <GraduationCap className="w-4 h-4 inline mr-1" />
                      Don't know your login email?
                    </p>
                    <button
                      type="button"
                      onClick={() => setShowAccountLookup(true)}
                      className="text-sm text-blue-600 hover:text-blue-800 font-medium underline"
                    >
                      Look up your account →
                    </button>
                  </div>
                )}

                {/* Account Lookup Form */}
                {signInType === "academy" && showAccountLookup && (
                  <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="font-semibold text-blue-900">Find Your Account</h4>
                      <button
                        type="button"
                        onClick={() => {
                          setShowAccountLookup(false);
                          setLookupResult(null);
                          setLookupEmail("");
                          setLookupChildReg("");
                        }}
                        className="text-gray-500 hover:text-gray-700 text-sm"
                      >
                        ✕ Close
                      </button>
                    </div>
                    
                    <div className="space-y-3">
                      <div>
                        <label className="text-xs text-gray-600 block mb-1">Email used during registration</label>
                        <Input
                          type="email"
                          placeholder="your.email@example.com"
                          value={lookupEmail}
                          onChange={(e) => setLookupEmail(e.target.value)}
                          className="border-blue-200 focus:border-blue-500"
                        />
                      </div>
                      
                      <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                          <div className="w-full border-t border-blue-200"></div>
                        </div>
                        <div className="relative flex justify-center text-xs">
                          <span className="px-2 bg-blue-50 text-gray-500">OR</span>
                        </div>
                      </div>
                      
                      <div>
                        <label className="text-xs text-gray-600 block mb-1">Child's Registration Number</label>
                        <Input
                          type="text"
                          placeholder="e.g., 15C0001"
                          value={lookupChildReg}
                          onChange={(e) => setLookupChildReg(e.target.value.toUpperCase())}
                          className="border-blue-200 focus:border-blue-500"
                        />
                      </div>
                      
                      <Button
                        type="button"
                        onClick={handleAccountLookup}
                        disabled={lookingUp || (!lookupEmail && !lookupChildReg)}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        {lookingUp ? "Looking up..." : "Find My Account"}
                      </Button>
                    </div>
                    
                    {/* Lookup Result */}
                    {lookupResult && (
                      <div className={`mt-4 p-3 rounded-lg ${
                        lookupResult.found 
                          ? lookupResult.has_account 
                            ? "bg-green-100 border border-green-300" 
                            : "bg-yellow-100 border border-yellow-300"
                          : "bg-red-100 border border-red-300"
                      }`}>
                        {lookupResult.found && lookupResult.has_account ? (
                          <div>
                            <p className="text-green-800 font-semibold mb-2">✓ Account Found!</p>
                            <div className="bg-white p-3 rounded border border-green-200 mb-2">
                              <p className="text-xs text-gray-500">Your login email:</p>
                              <p className="text-lg font-mono font-bold text-green-700">{lookupResult.masked_email}</p>
                            </div>
                            {lookupResult.children && lookupResult.children.length > 0 && (
                              <div className="text-sm text-green-800">
                                <p className="font-medium">Registered children:</p>
                                <ul className="list-disc list-inside">
                                  {lookupResult.children.map((child, idx) => (
                                    <li key={idx}>{child.name} ({child.registration_number})</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            <p className="text-xs text-green-700 mt-2">{lookupResult.hint}</p>
                            <Button
                              type="button"
                              onClick={() => {
                                // Pre-fill email and close lookup
                                if (lookupResult.masked_email) {
                                  // Can't pre-fill masked email, but close the lookup
                                  setShowAccountLookup(false);
                                }
                              }}
                              className="mt-2 w-full bg-green-600 hover:bg-green-700 text-white text-sm"
                            >
                              Got it! Sign in now
                            </Button>
                          </div>
                        ) : lookupResult.found && !lookupResult.has_account ? (
                          <div>
                            <p className="text-yellow-800 font-semibold mb-2">Child Found - No Account Yet</p>
                            <p className="text-sm text-yellow-700">{lookupResult.message}</p>
                            <p className="text-xs text-yellow-600 mt-2">{lookupResult.hint}</p>
                            <Button
                              type="button"
                              onClick={() => {
                                setShowAccountLookup(false);
                                setAuthTab("register");
                              }}
                              className="mt-2 w-full bg-yellow-600 hover:bg-yellow-700 text-white text-sm"
                            >
                              Register Now
                            </Button>
                          </div>
                        ) : (
                          <div>
                            <p className="text-red-800 font-semibold mb-1">Not Found</p>
                            <p className="text-sm text-red-700">{lookupResult.message}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Login Form */}
                <form onSubmit={handleEmailLogin} className="space-y-4">
                  <div>
                    <Input
                      type="text"
                      placeholder={signInType === "academy" ? "Email" : "Email or Membership Number"}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      data-testid="login-email-input"
                      className="border-gray-300 focus:border-yellow-500 focus:ring-yellow-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {signInType === "academy" 
                        ? "Use the email you registered with"
                        : "Use your email or your child's membership number (e.g., 15C0001)"
                      }
                    </p>
                  </div>
                  <div className="relative">
                    <Input
                      type={showLoginPassword ? "text" : "password"}
                      placeholder="Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      data-testid="login-password-input"
                      className="border-gray-300 focus:border-yellow-500 focus:ring-yellow-500 pr-12"
                    />
                    <button
                      type="button"
                      onClick={() => setShowLoginPassword(!showLoginPassword)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 p-2 z-10"
                    >
                      {showLoginPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  <div className="text-right">
                    <a
                      href="/forgot-password"
                      className="text-sm text-blue-600 hover:text-blue-400 font-medium"
                    >
                      Forgot Password?
                    </a>
                  </div>
                  <Button
                    type="submit"
                    disabled={loading}
                    className={`w-full font-semibold ${
                      signInType === "academy"
                        ? "bg-blue-600 hover:bg-blue-700 text-white"
                        : "bg-yellow-500/100 hover:bg-yellow-600 text-black"
                    }`}
                    data-testid="login-submit-button"
                  >
                    {loading ? "Logging in..." : "Login"}
                  </Button>
                </form>

                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white text-gray-500">Or</span>
                  </div>
                </div>

                <Button
                  onClick={handleGoogleLogin}
                  variant="outline"
                  className="w-full border-2 border-gray-300 hover:border-yellow-500 hover:bg-yellow-500/10"
                  data-testid="google-login-button"
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  Google Login
                </Button>
              </TabsContent>

              <TabsContent value="register">
                {/* Progress Indicator */}
                {registrationStep > 0 && registrationStep <= 4 && isSccMember !== false && (
                  <div className="mb-6">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-xs text-gray-600">Step {registrationStep} of {isFootballSection === false ? 4 : 4}</span>
                    </div>
                    <div className="w-full bg-slate-700 rounded-full h-2">
                      <div 
                        className="bg-yellow-500/100 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${(registrationStep / 4) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                )}
                
                {/* Step 1: Role Selection - What do you want to register for? */}
                {registrationStep === 1 && (
                  <div className="space-y-6">
                    <div className="text-center">
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">Welcome to {branding.clubName}</h3>
                      <p className="text-gray-600">What would you like to register for?</p>
                    </div>
                    
                    <div className="space-y-3">
                      {/* Academy Parent - most common */}
                      <button
                        type="button"
                        onClick={() => {
                          window.location.href = '/academy/register';
                        }}
                        className="w-full p-5 border-2 border-blue-300 rounded-lg hover:border-blue-500 hover:bg-blue-500/10 transition-all text-left group"
                      >
                        <div className="flex items-center gap-4">
                          <div className="text-3xl">⚽</div>
                          <div>
                            <div className="font-bold text-gray-900 text-lg group-hover:text-blue-400">Academy Registration</div>
                            <div className="text-sm text-gray-600 mt-1">Register your child for football training</div>
                            <div className="text-xs text-blue-500 mt-1">SCC members enjoy discounted rates</div>
                          </div>
                        </div>
                      </button>
                      
                      {/* Section Member */}
                      <button
                        type="button"
                        onClick={() => {
                          setRegistrationStep(15);
                        }}
                        className="w-full p-5 border-2 border-gray-300 rounded-lg hover:border-green-500 hover:bg-green-500/10 transition-all text-left group"
                      >
                        <div className="flex items-center gap-4">
                          <div className="text-3xl">🏆</div>
                          <div>
                            <div className="font-bold text-gray-900 text-lg group-hover:text-green-400">Football Section Member</div>
                            <div className="text-sm text-gray-600 mt-1">Join the adult football section</div>
                            <span className="inline-block mt-1 px-2 py-0.5 bg-green-500/20 text-green-400 rounded-full text-xs font-bold">FREE</span>
                          </div>
                        </div>
                      </button>
                    </div>
                    
                    <p className="text-xs text-gray-400 text-center">
                      Already have an account? Switch to the <button type="button" onClick={() => setAuthTab("login")} className="text-blue-400 underline">Login</button> tab.
                    </p>
                  </div>
                )}
                
                {/* Step 15: Section member - SCC member check */}
                {registrationStep === 15 && (
                  <div className="space-y-6">
                    <button type="button" onClick={() => setRegistrationStep(1)}
                      className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1">
                      ← Back
                    </button>
                    <div className="text-center">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">Are you an SCC Club member?</h3>
                      <p className="text-sm text-gray-500 max-w-md mx-auto">
                        An SCC member pays monthly subscription fees to Singapore Cricket Club.
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <button type="button"
                        onClick={() => { setIsSccMember(true); setRegistrationStep(2); }}
                        className="p-6 border-2 border-gray-300 rounded-lg hover:border-yellow-500 hover:bg-yellow-500/10 transition-all text-center group">
                        <div className="font-bold text-gray-900 text-lg group-hover:text-yellow-400">Yes</div>
                        <div className="text-xs text-gray-600 mt-2">I have a membership number</div>
                      </button>
                      <button type="button"
                        onClick={() => { setIsSccMember(false); setRegistrationStep(10); }}
                        className="p-6 border-2 border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-500/10 transition-all text-center group">
                        <div className="font-bold text-gray-900 text-lg group-hover:text-blue-400">No</div>
                        <div className="text-xs text-gray-600 mt-2">Not an SCC Club member</div>
                      </button>
                    </div>
                  </div>
                )}
                
                {/* Step 2: Enter membership number */}
                {registrationStep === 2 && isSccMember && (
                  <div className="space-y-4">
                    <button
                      type="button"
                      onClick={() => {
                        setRegistrationStep(1);
                        setMembershipCheckResult(null);
                      }}
                      className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
                    >
                      ← Back
                    </button>
                    
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">
                        {branding.isSccBranding ? 'Enter Your SCC Membership Number' : 'Enter Your Membership ID'}
                      </h3>
                      <p className="text-sm text-gray-600 mb-4">
                        {branding.isSccBranding ? 'This is required to verify your SCC membership' : 'This helps us verify your membership'}
                      </p>
                      
                      <Input
                        type="text"
                        placeholder={`Enter your ${branding.membershipLabel}`}
                        value={membershipNumber}
                        onChange={(e) => {
                          setMembershipNumber(e.target.value);
                          setMembershipCheckResult(null); // Clear previous result on change
                        }}
                        className={`border-gray-300 text-lg ${branding.isSccBranding ? 'focus:border-yellow-500 focus:ring-yellow-500' : 'focus:border-blue-500 focus:ring-blue-500'}`}
                      />
                      {branding.isSccBranding && (
                        <p className="text-sm text-red-600 mt-2 font-semibold">* REQUIRED</p>
                      )}
                      <p className="text-xs text-slate-500 mt-2 flex items-center gap-1">
                        <span>🔒</span> Your membership number is kept private and not visible to other members
                      </p>
                    </div>
                    
                    {/* Already Registered Warning */}
                    {membershipCheckResult?.exists && (
                      <div className="bg-orange-500/10 border-2 border-orange-500/30 rounded-lg p-4 space-y-3">
                        <div className="flex items-start gap-3">
                          <div className="text-2xl">⚠️</div>
                          <div>
                            <h4 className="font-bold text-orange-400">Already Registered!</h4>
                            <p className="text-sm text-orange-400 mt-1">
                              This membership number is already registered in our system.
                            </p>
                          </div>
                        </div>
                        
                        <div className="bg-white rounded-lg p-3 border border-orange-500/30">
                          <p className="text-sm text-gray-700 mb-2">
                            <strong>Your login credentials:</strong>
                          </p>
                          <div className="space-y-1 text-sm">
                            <p>📧 Email: <span className="font-mono bg-slate-800 px-2 py-0.5 rounded">{membershipCheckResult.masked_email}</span></p>
                            <p>🔑 Password: <span className="text-gray-600">Your SCC membership number</span></p>
                          </div>
                        </div>
                        
                        <Button
                          type="button"
                          onClick={() => {
                            resetForm();
                            setAuthTab("login"); // Switch to login tab
                          }}
                          className="w-full bg-yellow-500/100 hover:bg-yellow-600 text-black font-bold"
                        >
                          Go to Login →
                        </Button>
                        
                        <p className="text-xs text-center text-gray-500">
                          Forgot your email? Contact membership@sccfootball.com
                        </p>
                      </div>
                    )}
                    
                    {!membershipCheckResult?.exists && (
                      <Button
                        type="button"
                        disabled={checkingMembership}
                        onClick={async () => {
                          const canProceed = await checkMembershipNumber();
                          if (canProceed) {
                            setRegistrationStep(3);
                          }
                        }}
                        className="w-full bg-yellow-500/100 hover:bg-yellow-600 text-black font-bold text-lg py-6"
                      >
                        {checkingMembership ? "Checking..." : "Continue →"}
                      </Button>
                    )}
                  </div>
                )}
                
                {/* Step 3: Football section member? */}
                {registrationStep === 3 && isSccMember && (
                  <div className="space-y-6">
                    <button
                      type="button"
                      onClick={() => setRegistrationStep(2)}
                      className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
                    >
                      ← Back
                    </button>
                    
                    <div className="text-center">
                      <p className="font-bold text-gray-900 text-xl mb-2">Are you a Football Section member?</p>
                      <p className="text-sm text-gray-600">Registered with SCC Football Section</p>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <button
                        type="button"
                        onClick={() => {
                          setIsFootballSection(true);
                          setSectionType("football");
                          setRegistrationStep(31); // NEW: Go to "What brings you here?" step
                        }}
                        className="p-8 border-2 border-gray-300 rounded-lg hover:border-green-500 hover:bg-green-500/10 transition-all text-center group"
                      >
                        <div className="text-5xl mb-3">⚽</div>
                        <div className="font-bold text-gray-900 text-lg group-hover:text-green-400">Yes</div>
                        <div className="text-sm text-gray-600 mt-2">Football Section</div>
                        <div className="mt-3 px-4 py-2 bg-green-500/20 text-green-400 rounded-full text-sm font-bold">FREE ACCESS</div>
                      </button>
                      
                      <button
                        type="button"
                        onClick={() => {
                          setIsFootballSection(false);
                          setSectionType("non-football");
                          setRegistrationStep(6);
                        }}
                        className="p-8 border-2 border-gray-300 rounded-lg hover:border-yellow-500 hover:bg-yellow-500/10 transition-all text-center group"
                      >
                        <div className="text-5xl mb-3">🏃</div>
                        <div className="font-bold text-gray-900 text-lg group-hover:text-yellow-400">No</div>
                        <div className="text-sm text-gray-600 mt-2">Other Section</div>
                      </button>
                    </div>
                  </div>
                )}
                
                {/* Step 31: Section Member - What brings you here? (Multi-select) */}
                {registrationStep === 31 && isFootballSection && (
                  <div className="space-y-6">
                    <button
                      type="button"
                      onClick={() => setRegistrationStep(3)}
                      className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
                    >
                      ← Back
                    </button>
                    
                    <div className="text-center mb-6">
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">What brings you here?</h3>
                      <p className="text-gray-600">Select all that apply</p>
                    </div>
                    
                    <div className="space-y-4">
                      {/* Section Member Registration */}
                      <label 
                        className={`flex items-start gap-4 p-5 border-2 rounded-lg cursor-pointer transition-all ${
                          registrationOptions.sectionMember 
                            ? 'border-green-500 bg-green-500/10' 
                            : 'border-gray-300 hover:border-green-500/30 hover:bg-green-500/10/50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={registrationOptions.sectionMember}
                          onChange={(e) => setRegistrationOptions(prev => ({
                            ...prev,
                            sectionMember: e.target.checked
                          }))}
                          className="w-5 h-5 mt-1 accent-green-600"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-2xl">⚽</span>
                            <span className="font-bold text-gray-900 text-lg">Register as Section Member</span>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">Get digital access and select your team</p>
                          <span className="inline-block mt-2 px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-xs font-bold">FREE</span>
                        </div>
                      </label>
                      
                      {/* Academy Parent Registration */}
                      <label 
                        className={`flex items-start gap-4 p-5 border-2 rounded-lg cursor-pointer transition-all ${
                          registrationOptions.academyParent 
                            ? 'border-blue-500 bg-blue-500/10' 
                            : 'border-gray-300 hover:border-blue-500/30 hover:bg-blue-500/10/50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={registrationOptions.academyParent}
                          onChange={(e) => setRegistrationOptions(prev => ({
                            ...prev,
                            academyParent: e.target.checked
                          }))}
                          className="w-5 h-5 mt-1 accent-blue-600"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-2xl">👪</span>
                            <span className="font-bold text-slate-800 dark:text-white text-lg">Register my child for Academy</span>
                          </div>
                          <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">Enroll your child in SCC Football Academy</p>
                          <span className="inline-block mt-2 px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full text-xs font-bold">SECTION MEMBER DISCOUNT</span>
                        </div>
                      </label>
                      
                      {/* Coach Registration */}
                      <label 
                        className={`flex items-start gap-4 p-5 border-2 rounded-lg cursor-pointer transition-all ${
                          registrationOptions.coach 
                            ? 'border-yellow-500 bg-yellow-500/10' 
                            : 'border-gray-300 dark:border-slate-600 hover:border-yellow-500/30 hover:bg-yellow-500/10/50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={registrationOptions.coach}
                          onChange={(e) => setRegistrationOptions(prev => ({
                            ...prev,
                            coach: e.target.checked
                          }))}
                          className="w-5 h-5 mt-1 accent-yellow-600"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-2xl">👨‍🏫</span>
                            <span className="font-bold text-slate-800 dark:text-white text-lg">Register as a Coach</span>
                          </div>
                          <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">Apply to join our coaching team</p>
                          <span className="inline-block mt-2 px-3 py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-xs font-bold">REQUIRES APPROVAL</span>
                        </div>
                      </label>
                    </div>
                    
                    {/* Info about combined registration */}
                    {(registrationOptions.sectionMember && registrationOptions.academyParent) && (
                      <div className="bg-purple-500/10 border-l-4 border-purple-400 p-4 rounded-r-lg">
                        <p className="text-sm text-purple-400">
                          <strong>Combined Registration:</strong> Your section membership will be verified first. 
                          Once approved, your child's academy registration will be processed with the section member discount.
                        </p>
                      </div>
                    )}
                    
                    <Button
                      type="button"
                      disabled={!registrationOptions.sectionMember && !registrationOptions.academyParent && !registrationOptions.coach}
                      onClick={() => {
                        // If only academy parent selected, redirect to academy registration
                        if (registrationOptions.academyParent && !registrationOptions.sectionMember && !registrationOptions.coach) {
                          window.location.href = '/academy/start-registration';
                          return;
                        }
                        
                        // If only coach selected, go to coach form
                        if (registrationOptions.coach && !registrationOptions.sectionMember && !registrationOptions.academyParent) {
                          setRegistrationStep(11);
                          return;
                        }
                        
                        // Otherwise, go to section member registration form (Step 4)
                        // which will handle combined flows
                        setRegistrationStep(4);
                      }}
                      className="w-full bg-yellow-500/100 hover:bg-yellow-600 text-black font-bold text-lg py-6 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Continue →
                    </Button>
                    
                    <p className="text-xs text-gray-500 text-center">
                      Select at least one option to continue
                    </p>
                  </div>
                )}
                
                {/* Step 4: Football Section Registration Form */}
                {registrationStep === 4 && isFootballSection && (
                  <form onSubmit={async (e) => {
                    e.preventDefault();
                    
                    // Basic validation
                    if (!email || !password || !givenName || !lastName || !birthdate) {
                      toast.error("Please fill in all required fields");
                      return;
                    }
                    
                    setLoading(true);
                    try {
                      // Generate linked registration ID if multiple options selected
                      const hasMultiple = (registrationOptions.sectionMember && registrationOptions.academyParent) ||
                                        (registrationOptions.sectionMember && registrationOptions.coach) ||
                                        (registrationOptions.academyParent && registrationOptions.coach);
                      const linkId = hasMultiple ? `link_${Date.now()}_${Math.random().toString(36).substr(2, 9)}` : null;
                      
                      // Always register as section member first
                      await axios.post(`${API}/auth/register`, {
                        email,
                        password,
                        name: `${givenName} ${lastName}`.trim(),
                        given_name: givenName,
                        last_name: lastName,
                        membership_number: membershipNumber,
                        phone,
                        birthdate,
                        joined_scc_date: joinedSccDate,
                        primary_team: primaryTeam,
                        second_team: secondTeam,
                        section_type: "football",
                        membership_type: 'section',
                        is_scc_member: true,
                        linked_registration_id: linkId,
                        registration_options: registrationOptions
                      });
                      
                      // If only section member, show success
                      if (!registrationOptions.academyParent && !registrationOptions.coach) {
                        toast.success("Registration successful! Please wait for admin authorization.");
                        setShowAuth(false);
                        resetForm();
                        return;
                      }
                      
                      // If coach also selected, submit coach application
                      if (registrationOptions.coach) {
                        await axios.post(`${API}/contact/coach-application`, {
                          given_name: givenName,
                          last_name: lastName,
                          full_name: `${givenName} ${lastName}`.trim(),
                          phone: phone,
                          email: email,
                          birthdate: birthdate,
                          nroc_registered: false,
                          coaching_certifications: [],
                          currently_coaching: [],
                          linked_registration_id: linkId,
                          is_section_member: true,
                          membership_number: membershipNumber
                        });
                      }
                      
                      // If academy parent also selected, redirect to academy registration
                      if (registrationOptions.academyParent) {
                        toast.success("Section member registration submitted! Now complete your child's academy registration.");
                        // Store the linked registration info AND parent details for the academy wizard
                        sessionStorage.setItem('linked_registration', JSON.stringify({
                          linked_registration_id: linkId,
                          parent_email: email,
                          parent_given_name: givenName,
                          parent_last_name: lastName,
                          parent_phone: phone,
                          membership_number: membershipNumber,
                          is_section_member: true,  // Football Section member - gets section member discount
                          is_section_member_pending: true
                        }));
                        window.location.href = '/academy/start-registration';
                        return;
                      }
                      
                      toast.success("Registration submitted! Please wait for admin authorization.");
                      setShowAuth(false);
                      resetForm();
                    } catch (error) {
                      console.error("Registration error:", error);
                      toast.error(error.response?.data?.detail || "Registration failed");
                    } finally {
                      setLoading(false);
                    }
                  }} className="space-y-4">
                    <button
                      type="button"
                      onClick={() => setRegistrationStep(31)}
                      className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
                    >
                      ← Back
                    </button>
                    
                    {/* Show what's being registered */}
                    <div className="bg-green-500/10 border-2 border-green-500/30 rounded-lg p-4">
                      <p className="text-sm text-green-400 font-semibold">✓ Football Section Member Registration - FREE</p>
                      <p className="text-xs text-green-400 mt-1">Complete your details below</p>
                      {(registrationOptions.academyParent || registrationOptions.coach) && (
                        <div className="mt-2 pt-2 border-t border-green-500/30">
                          <p className="text-xs text-green-600">Also registering for:</p>
                          <div className="flex flex-wrap gap-2 mt-1">
                            {registrationOptions.academyParent && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded text-xs">
                                👪 Academy Parent
                              </span>
                            )}
                            {registrationOptions.coach && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-500/20 text-yellow-400 rounded text-xs">
                                👨‍🏫 Coach
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Input
                          type="text"
                          placeholder="Given Name"
                          value={givenName}
                          onChange={(e) => {
                            setGivenName(e.target.value);
                            setName(`${e.target.value} ${lastName}`.trim());
                          }}
                          required
                          className="border-gray-300 focus:border-yellow-500"
                        />
                        <p className="text-xs text-red-600 mt-1 font-semibold">* REQUIRED</p>
                      </div>
                      <div>
                        <Input
                          type="text"
                          placeholder="Last Name"
                          value={lastName}
                          onChange={(e) => {
                            setLastName(e.target.value);
                            setName(`${givenName} ${e.target.value}`.trim());
                          }}
                          required
                          className="border-gray-300 focus:border-yellow-500"
                        />
                        <p className="text-xs text-red-600 mt-1 font-semibold">* REQUIRED</p>
                      </div>
                    </div>
                    
                    <div>
                      <Input
                        type="email"
                        placeholder="Email Address"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className="border-gray-300 focus:border-yellow-500"
                      />
                      <p className="text-xs text-red-600 mt-1 font-semibold">* REQUIRED</p>
                    </div>
                    
                    <div>
                      <div className="relative">
                        <Input
                          type={showPassword ? "text" : "password"}
                          placeholder="Password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                          className="border-gray-300 focus:border-yellow-500 pr-12"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 p-2 z-10"
                        >
                          {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                      <p className="text-xs text-red-600 mt-1 font-semibold">* REQUIRED (min 6 characters)</p>
                    </div>
                    
                    <div>
                      <Input
                        type="tel"
                        placeholder="Phone Number"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        className="border-gray-300 focus:border-yellow-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">Optional</p>
                    </div>
                    
                    <div>
                      <Input
                        type="date"
                        value={birthdate}
                        onChange={(e) => setBirthdate(e.target.value)}
                        required
                        className="border-gray-300 focus:border-yellow-500"
                      />
                      <p className="text-xs text-red-600 mt-1 font-semibold">* REQUIRED - Your Birthday</p>
                    </div>
                    
                    <div>
                      <Input
                        type="date"
                        placeholder="Date Joined SCC"
                        value={joinedSccDate}
                        onChange={(e) => setJoinedSccDate(e.target.value)}
                        className="border-gray-300 focus:border-yellow-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">When did you join SCC? (Optional)</p>
                    </div>
                    
                    <div>
                      <select
                        value={primaryTeam}
                        onChange={(e) => setPrimaryTeam(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:border-yellow-500 focus:ring-yellow-500"
                      >
                        <option value="">Select Primary Team</option>
                        <option value="non-playing">🏃 Non-Playing Section Member</option>
                        {teams.map((team) => (
                          <option key={team.id} value={team.id}>
                            ⚽ {team.name}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">Select "Non-Playing" if you don't play in matches</p>
                    </div>
                    
                    {primaryTeam && primaryTeam !== 'non-playing' && (
                      <div>
                        <select
                          value={secondTeam}
                          onChange={(e) => setSecondTeam(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:border-yellow-500 focus:ring-yellow-500"
                        >
                          <option value="">Select Second Team (Optional)</option>
                          {teams.filter(t => t.id !== primaryTeam).map((team) => (
                            <option key={team.id} value={team.id}>
                              {team.name}
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">Your secondary team (if any)</p>
                      </div>
                    )}
                    
                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-green-600 hover:bg-green-700 text-white font-bold text-lg py-6"
                    >
                      {loading ? "Registering..." : 
                        registrationOptions.academyParent ? "Continue to Academy Registration →" : 
                        "Complete Registration"}
                    </Button>
                    
                    <p className="text-sm text-gray-600 text-center mt-2">
                      Note: Admin must authorize your account before you can login
                    </p>
                  </form>
                )}
                {/* Step 6: Non-Football Section - Choose Intent */}
                {registrationStep === 6 && !isFootballSection && (
                  <div className="space-y-6">
                    <button
                      type="button"
                      onClick={() => setRegistrationStep(3)}
                      className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
                    >
                      ← Back
                    </button>
                    
                    <div className="bg-yellow-500/10 border-2 border-yellow-500/30 rounded-lg p-4 mb-4">
                      <p className="text-sm text-yellow-400 font-semibold">⚠️ You're an SCC member but not in Football Section</p>
                      <p className="text-xs text-yellow-400 mt-1">Choose what you'd like to do below</p>
                    </div>
                    
                    <div className="text-center">
                      <p className="font-bold text-gray-900 text-xl mb-2">What brings you here?</p>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Digital Non-Playing Membership */}
                      <button
                        type="button"
                        onClick={() => {
                          setMembershipIntent('non-playing');
                          setRegistrationStep(7);
                        }}
                        className="p-6 border-2 border-gray-300 rounded-lg hover:border-purple-500 hover:bg-purple-500/10 transition-all text-left group"
                      >
                        <div className="text-4xl mb-3">💻</div>
                        <div className="font-bold text-gray-900 text-lg group-hover:text-purple-400">Digital Non-Playing Membership</div>
                        <div className="text-sm text-gray-600 mt-2 mb-3">Access online portal and benefits</div>
                        <div className="mt-3 px-4 py-2 bg-purple-500/20 text-purple-400 rounded-lg text-center font-bold">$126/year</div>
                        <ul className="text-xs text-gray-600 mt-3 space-y-1">
                          <li>• Digital membership card</li>
                          <li>• Access to online portal</li>
                          <li>• Member discounts</li>
                          <li>• Event participation</li>
                        </ul>
                      </button>
                      
                      {/* Academy Parent - Next to Digital Membership */}
                      <button
                        type="button"
                        onClick={() => {
                          // Go to new step to capture member info first for discount verification
                          setRegistrationStep(61);
                        }}
                        className="p-6 border-2 border-gray-300 dark:border-slate-600 rounded-lg hover:border-blue-500 hover:bg-blue-500/10 transition-all text-left group relative"
                      >
                        <div className="absolute top-2 right-2 bg-green-500/100 text-white text-xs px-2 py-1 rounded">New!</div>
                        <div className="text-4xl mb-3">👪</div>
                        <div className="font-bold text-slate-800 dark:text-white text-lg group-hover:text-blue-400">Register my child for Academy</div>
                        <div className="text-sm text-slate-600 dark:text-slate-300 mt-2 mb-3">Enroll your child in youth football</div>
                        <div className="mt-3 px-4 py-2 bg-blue-500/20 text-blue-400 rounded-lg text-center font-bold">Club Member Discount</div>
                        <ul className="text-xs text-slate-600 dark:text-slate-400 mt-3 space-y-1">
                          <li>• Professional coaching</li>
                          <li>• Age-appropriate training</li>
                          <li>• SCC facilities access</li>
                          <li>• Competition opportunities</li>
                        </ul>
                      </button>
                      
                      {/* Playing Membership - Next to Coach */}
                      <button
                        type="button"
                        onClick={() => {
                          setMembershipIntent('playing');
                          setRegistrationStep(8);
                        }}
                        className="p-6 border-2 border-gray-300 dark:border-slate-600 rounded-lg hover:border-green-500 hover:bg-green-500/10 transition-all text-left group"
                      >
                        <div className="text-4xl mb-3">⚽</div>
                        <div className="font-bold text-slate-800 dark:text-white text-lg group-hover:text-green-400">Playing Membership</div>
                        <div className="text-sm text-slate-600 dark:text-slate-300 mt-2 mb-3">Full football section membership</div>
                        <div className="mt-3 px-4 py-2 bg-green-500/20 text-green-400 rounded-lg text-center font-bold">Contact Required</div>
                        <ul className="text-xs text-slate-600 dark:text-slate-400 mt-3 space-y-1">
                          <li>• Full section privileges</li>
                          <li>• Play in games</li>
                          <li>• All digital benefits</li>
                          <li>• Official SCC Football member</li>
                        </ul>
                      </button>
                      
                      {/* Coach Application */}
                      <button
                        type="button"
                        onClick={() => {
                          setUserType('coach');
                          setRegistrationStep(11);
                        }}
                        className="p-6 border-2 border-gray-300 dark:border-slate-600 rounded-lg hover:border-yellow-500 hover:bg-yellow-500/10 transition-all text-left group"
                      >
                        <div className="text-4xl mb-3">👨‍🏫</div>
                        <div className="font-bold text-slate-800 dark:text-white text-lg group-hover:text-yellow-400">Register as a Coach</div>
                        <div className="text-sm text-slate-600 dark:text-slate-300 mt-2 mb-3">Apply to join our coaching team</div>
                        <div className="mt-3 px-4 py-2 bg-yellow-500/20 text-yellow-400 rounded-lg text-center font-bold">Requires Approval</div>
                        <ul className="text-xs text-slate-600 dark:text-slate-400 mt-3 space-y-1">
                          <li>• Train young players</li>
                          <li>• Flexible scheduling</li>
                          <li>• Professional development</li>
                          <li>• Part of coaching network</li>
                        </ul>
                      </button>
                    </div>
                  </div>
                )}
                
                {/* Step 61: Non-Football Section - Academy Registration with Membership Verification */}
                {registrationStep === 61 && !isFootballSection && (
                  <form onSubmit={async (e) => {
                    e.preventDefault();
                    
                    // Basic validation
                    if (!email || !password || !givenName || !lastName || !birthdate) {
                      toast.error("Please fill in all required fields");
                      return;
                    }
                    
                    setLoading(true);
                    try {
                      // Generate linked registration ID for membership verification + academy
                      const linkId = `link_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
                      
                      // Register as SCC member (non-football section) for verification
                      await axios.post(`${API}/auth/register`, {
                        email,
                        password,
                        name: `${givenName} ${lastName}`.trim(),
                        given_name: givenName,
                        last_name: lastName,
                        membership_number: membershipNumber,
                        phone,
                        birthdate,
                        membership_type: 'scc_member',
                        section_type: "non-football",
                        is_scc_member: true,
                        linked_registration_id: linkId,
                        registration_options: { sectionMember: false, academyParent: true, coach: false, sccMemberVerification: true }
                      });
                      
                      toast.success("Membership details submitted! Now complete your child's academy registration.");
                      
                      // Store linked registration info AND parent details for academy wizard
                      sessionStorage.setItem('linked_registration', JSON.stringify({
                        linked_registration_id: linkId,
                        parent_email: email,
                        parent_given_name: givenName,
                        parent_last_name: lastName,
                        parent_phone: phone,
                        membership_number: membershipNumber,
                        is_scc_member_pending: true,
                        is_non_football_section: true
                      }));
                      
                      window.location.href = '/academy/start-registration';
                    } catch (error) {
                      console.error("Registration error:", error);
                      toast.error(error.response?.data?.detail || "Registration failed");
                    } finally {
                      setLoading(false);
                    }
                  }} className="space-y-4">
                    <button
                      type="button"
                      onClick={() => setRegistrationStep(6)}
                      className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
                    >
                      ← Back
                    </button>
                    
                    <div className="bg-blue-500/10 border-2 border-blue-500/30 rounded-lg p-4">
                      <p className="text-sm text-blue-400 font-semibold">👪 Register Child for Academy - Club Member Discount</p>
                      <p className="text-xs text-blue-400 mt-1">Your SCC membership will be verified to apply the discount</p>
                    </div>
                    
                    <div className="bg-yellow-500/10 border-l-4 border-yellow-400 p-4">
                      <p className="text-sm text-yellow-400">
                        <strong>Approval Process:</strong> Your SCC membership will be verified first. 
                        Once approved, your child's academy registration will be processed with the club member discount.
                      </p>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Input
                          type="text"
                          placeholder="Your Given Name"
                          value={givenName}
                          onChange={(e) => {
                            setGivenName(e.target.value);
                            setName(`${e.target.value} ${lastName}`.trim());
                          }}
                          required
                          className="border-gray-300 focus:border-blue-500"
                        />
                        <p className="text-xs text-red-600 mt-1 font-semibold">* REQUIRED</p>
                      </div>
                      <div>
                        <Input
                          type="text"
                          placeholder="Your Last Name"
                          value={lastName}
                          onChange={(e) => {
                            setLastName(e.target.value);
                            setName(`${givenName} ${e.target.value}`.trim());
                          }}
                          required
                          className="border-gray-300 focus:border-blue-500"
                        />
                        <p className="text-xs text-red-600 mt-1 font-semibold">* REQUIRED</p>
                      </div>
                    </div>
                    
                    <div>
                      <Input
                        type="email"
                        placeholder="Your Email Address"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className="border-gray-300 focus:border-blue-500"
                      />
                      <p className="text-xs text-red-600 mt-1 font-semibold">* REQUIRED</p>
                    </div>
                    
                    <div>
                      <div className="relative">
                        <Input
                          type={showPassword ? "text" : "password"}
                          placeholder="Create Password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                          className="border-gray-300 focus:border-blue-500 pr-12"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 p-2 z-10"
                        >
                          {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                      <p className="text-xs text-red-600 mt-1 font-semibold">* REQUIRED (min 6 characters)</p>
                    </div>
                    
                    <div>
                      <Input
                        type="tel"
                        placeholder="Phone Number"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        className="border-gray-300 focus:border-blue-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">Optional</p>
                    </div>
                    
                    <div>
                      <Input
                        type="date"
                        value={birthdate}
                        onChange={(e) => setBirthdate(e.target.value)}
                        required
                        className="border-gray-300 focus:border-blue-500"
                      />
                      <p className="text-xs text-red-600 mt-1 font-semibold">* REQUIRED - Your Birthday</p>
                    </div>
                    
                    <div className="bg-slate-800/50 p-3 rounded-lg">
                      <p className="text-xs text-gray-600 mb-2 font-semibold">SCC Membership Number (from Step 2)</p>
                      <Input
                        type="text"
                        value={membershipNumber}
                        disabled
                        className="border-gray-300 bg-slate-800"
                      />
                      <p className="text-xs text-gray-500 mt-1">This will be verified for discount eligibility</p>
                    </div>
                    
                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold text-lg py-6"
                    >
                      {loading ? "Submitting..." : "Continue to Academy Registration →"}
                    </Button>
                    
                    <p className="text-sm text-gray-600 text-center mt-2">
                      Your membership will be verified before the discount is applied
                    </p>
                  </form>
                )}
                
                {/* Step 7: Non-Playing - Payment Upload */}
                {registrationStep === 7 && membershipIntent === 'non-playing' && (
                  <form onSubmit={handleEmailRegister} className="space-y-4">
                    <button
                      type="button"
                      onClick={() => setRegistrationStep(6)}
                      className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
                    >
                      ← Back
                    </button>
                    
                    <div className="bg-purple-500/10 border-2 border-purple-500/30 rounded-lg p-4">
                      <p className="text-sm text-purple-400 font-semibold">💳 Digital Non-Playing Membership - $126/year</p>
                    </div>
                    
                    {/* Disclaimer */}
                    <div className="bg-yellow-500/10 border-l-4 border-yellow-400 p-4">
                      <p className="text-sm font-bold text-yellow-300 mb-2">Important Disclaimer:</p>
                      <p className="text-xs text-yellow-400">
                        Digital membership is managed by SCC Football for access to benefits provided via online portal and does NOT include privileges as a full football section member signed up with Singapore Cricket Club.
                      </p>
                      <label className="flex items-start gap-2 mt-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={acceptedDisclaimer}
                          onChange={(e) => setAcceptedDisclaimer(e.target.checked)}
                          className="mt-1"
                        />
                        <span className="text-sm text-yellow-300">I understand and accept the terms above</span>
                      </label>
                    </div>
                    
                    {/* Payment Instructions */}
                    <div className="bg-white border-2 border-gray-300 rounded-lg p-4">
                      <h4 className="font-bold text-gray-900 mb-2">Payment Instructions:</h4>
                      <ol className="text-sm text-gray-700 space-y-2">
                        <li>1. PayNow to: <strong>"Football and More"</strong></li>
                        <li>2. Amount: <strong>$126.00</strong></li>
                        <li>3. Reference: Your SCC Membership Number (<strong>{membershipNumber}</strong>)</li>
                        <li>4. Upload payment screenshot below</li>
                      </ol>
                    </div>
                    
                    {/* File Upload */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">
                        Upload PayNow Payment Proof
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            if (file.size > 5000000) {
                              toast.error("File size must be less than 5MB");
                              return;
                            }
                            const reader = new FileReader();
                            reader.onloadend = () => {
                              setPaymentProof(reader.result);
                              toast.success("Payment proof uploaded!");
                            };
                            reader.readAsDataURL(file);
                          }
                        }}
                        className="w-full border-2 border-gray-300 rounded-lg p-2 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-yellow-500/10 file:text-yellow-400 hover:file:bg-yellow-500/20"
                      />
                      <p className="text-xs text-red-600 mt-1 font-semibold">* REQUIRED - Upload payment receipt</p>
                      {paymentProof && (
                        <div className="mt-2">
                          <p className="text-xs text-green-600">✓ Payment proof uploaded</p>
                        </div>
                      )}
                    </div>
                    
                    {/* Registration Form Fields */}
                    <div>
                      <Input
                        type="text"
                        placeholder="Full Name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                      />
                      <p className="text-xs text-red-600 mt-1 font-semibold">* REQUIRED</p>
                    </div>
                    
                    <div>
                      <Input
                        type="email"
                        placeholder="Email Address"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                      <p className="text-xs text-red-600 mt-1 font-semibold">* REQUIRED</p>
                    </div>
                    
                    <div>
                      <Input
                        type="password"
                        placeholder="Password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                      />
                      <p className="text-xs text-red-600 mt-1 font-semibold">* REQUIRED</p>
                    </div>
                    
                    <div>
                      <Input
                        type="tel"
                        placeholder="Phone Number"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                      />
                      <p className="text-xs text-gray-500 mt-1">Optional</p>
                    </div>
                    
                    <div>
                      <Input
                        type="date"
                        value={birthdate}
                        onChange={(e) => setBirthdate(e.target.value)}
                        required
                      />
                      <p className="text-xs text-red-600 mt-1 font-semibold">* REQUIRED - Your Birthday</p>
                    </div>
                    
                    <Button
                      type="submit"
                      disabled={loading || !acceptedDisclaimer}
                      className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold text-lg py-6"
                    >
                      {loading ? "Submitting..." : "Submit Registration"}
                    </Button>
                    
                    <p className="text-sm text-gray-600 text-center mt-2">
                      Admin will verify your payment and authorize your account
                    </p>
                  </form>
                )}
                
                {/* Step 8: Playing Membership - Email Enquiry */}
                {registrationStep === 8 && membershipIntent === 'playing' && (
                  <div className="space-y-4">
                    <button
                      type="button"
                      onClick={() => setRegistrationStep(6)}
                      className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
                    >
                      ← Back
                    </button>
                    
                    <div className="bg-green-500/10 border-2 border-green-500/30 rounded-lg p-4">
                      <p className="text-sm text-green-400 font-semibold">⚽ Playing Membership Registration</p>
                    </div>
                    
                    {/* Disclaimer */}
                    <div className="bg-yellow-500/10 border-l-4 border-yellow-400 p-4">
                      <p className="text-sm font-bold text-yellow-300 mb-2">Important Disclaimer:</p>
                      <p className="text-xs text-yellow-400 mb-3">
                        Digital membership is managed by SCC Football for access to benefits provided via online portal and does NOT include privileges as a full football section member signed up with Singapore Cricket Club.
                      </p>
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={acceptedDisclaimer}
                          onChange={(e) => setAcceptedDisclaimer(e.target.checked)}
                          className="mt-1"
                        />
                        <span className="text-sm text-yellow-300">I understand and accept the terms above</span>
                      </label>
                    </div>
                    
                    <div className="bg-white border-2 border-blue-500/30 rounded-lg p-6 text-center">
                      <div className="text-5xl mb-4">📧</div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">Contact Required</h3>
                      <p className="text-gray-700 mb-4">
                        Playing membership requires approval from the membership department.
                      </p>
                      <p className="text-sm text-gray-600 mb-4">
                        Click below to send your request. Our team will contact you with the next steps.
                      </p>
                    </div>
                    
                    <form onSubmit={handleEmailRegister} className="space-y-4">
                      <div>
                        <Input
                          type="text"
                          placeholder="Full Name"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          required
                        />
                        <p className="text-xs text-red-600 mt-1 font-semibold">* REQUIRED</p>
                      </div>
                      
                      <div>
                        <Input
                          type="email"
                          placeholder="Email Address"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                        />
                        <p className="text-xs text-red-600 mt-1 font-semibold">* REQUIRED</p>
                      </div>
                      
                      <div>
                        <Input
                          type="tel"
                          placeholder="Phone Number"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                        />
                        <p className="text-xs text-gray-500 mt-1">Optional</p>
                      </div>
                      
                      <div>
                        <Input
                          type="date"
                          value={birthdate}
                          onChange={(e) => setBirthdate(e.target.value)}
                          required
                        />
                        <p className="text-xs text-red-600 mt-1 font-semibold">* REQUIRED - Your Birthday</p>
                      </div>
                      
                      <Button
                        type="submit"
                        disabled={loading || !acceptedDisclaimer}
                        className="w-full bg-green-600 hover:bg-green-700 text-white font-bold text-lg py-6"
                      >
                        {loading ? "Sending..." : "Send Request to Membership Dept"}
                      </Button>
                    </form>
                  </div>
                )}
                
                {/* Step 10: Non-SCC Member - Choose User Type */}
                {registrationStep === 10 && !isSccMember && (
                  <div className="space-y-6">
                    <button
                      type="button"
                      onClick={() => setRegistrationStep(1)}
                      className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
                    >
                      ← Back
                    </button>
                    
                    <div className="text-center mb-6">
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">What brings you here?</h3>
                      <p className="text-gray-600">Select the option that best describes you</p>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <button
                        type="button"
                        onClick={() => {
                          setUserType('coach');
                          setRegistrationStep(11);
                        }}
                        className="p-8 border-2 border-gray-300 dark:border-slate-600 rounded-lg hover:border-yellow-500 hover:bg-yellow-500/10 transition-all text-center group"
                      >
                        <div className="text-5xl mb-3">👨‍🏫</div>
                        <div className="font-bold text-slate-800 dark:text-white text-lg group-hover:text-yellow-400">I'm a Coach</div>
                        <div className="text-sm text-slate-600 dark:text-slate-300 mt-2">Apply to join our coaching team</div>
                      </button>
                      
                      <button
                        type="button"
                        onClick={() => {
                          window.location.href = '/academy/start-registration';
                        }}
                        className="p-8 border-2 border-gray-300 dark:border-slate-600 rounded-lg hover:border-blue-500 hover:bg-blue-500/10 transition-all text-center group relative"
                      >
                        <div className="absolute top-2 right-2 bg-green-500/100 text-white text-xs px-2 py-1 rounded">New!</div>
                        <div className="text-5xl mb-3">👪</div>
                        <div className="font-bold text-slate-800 dark:text-white text-lg group-hover:text-blue-400">Academy Parent</div>
                        <div className="text-sm text-slate-600 dark:text-slate-300 mt-2">Enroll your child in our academy</div>
                      </button>
                    </div>
                    
                    <div className="bg-blue-500/10 rounded-lg p-4 mt-6 text-center">
                      <p className="text-sm text-slate-700 dark:text-slate-300">
                        <strong>Note:</strong> To join as a regular member, you need to be an SCC member first.
                      </p>
                      <Button
                        type="button"
                        onClick={() => {
                          setShowAuth(false);
                          document.querySelector('[data-testid="membership-enquiry-section"]')?.scrollIntoView({ behavior: 'smooth' });
                        }}
                        variant="outline"
                        className="mt-3 border-blue-600 text-blue-600 hover:bg-blue-500/10"
                      >
                        Send Membership Enquiry
                      </Button>
                    </div>
                  </div>
                )}
                
                {/* Step 11: Coach Application Form */}
                {registrationStep === 11 && (
                  <div className="space-y-6">
                    <button
                      type="button"
                      onClick={() => {
                        // Go back based on where the user came from
                        if (isFootballSection) {
                          setRegistrationStep(31); // Section member came from Step 31
                        } else if (isSccMember) {
                          setRegistrationStep(6); // Non-football SCC member came from Step 6
                        } else {
                          setRegistrationStep(10); // Non-SCC member came from Step 10
                        }
                      }}
                      className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
                    >
                      ← Back
                    </button>
                    
                    <div className="text-center mb-4">
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">👨‍🏫 Coach Application</h3>
                      <p className="text-gray-600">Tell us about your coaching background</p>
                      {isFootballSection && (
                        <p className="text-sm text-green-600 mt-2">✓ Football Section Member</p>
                      )}
                    </div>
                    
                    <form onSubmit={async (e) => {
                      e.preventDefault();
                      if (!coachGivenName || !coachLastName || !coachEmail || !coachPhone || !coachBirthdate) {
                        toast.error("Please fill in all required fields (given name, last name, email, phone, birthdate)");
                        return;
                      }
                      
                      setLoading(true);
                      try {
                        await axios.post(`${API}/contact/coach-application`, {
                          given_name: coachGivenName,
                          last_name: coachLastName,
                          full_name: `${coachGivenName} ${coachLastName}`.trim(),
                          phone: coachPhone,
                          email: coachEmail,
                          birthdate: coachBirthdate,
                          nroc_registered: nrocRegistered,
                          coaching_certifications: coachingCertifications,
                          currently_coaching: currentlyCoaching
                        });
                        
                        toast.success("Application submitted successfully! We will be in touch soon.");
                        resetForm();
                        setShowAuth(false);
                      } catch (error) {
                        console.error("Coach application error:", error);
                        toast.error(error.response?.data?.detail || "Failed to submit application");
                      } finally {
                        setLoading(false);
                      }
                    }} className="space-y-4">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium mb-2">Given Name *</label>
                          <Input
                            value={coachGivenName}
                            onChange={(e) => setCoachGivenName(e.target.value)}
                            placeholder="Johnny"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-2">Last Name *</label>
                          <Input
                            value={coachLastName}
                            onChange={(e) => setCoachLastName(e.target.value)}
                            placeholder="Tan"
                            required
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-2">Email *</label>
                        <Input
                          type="email"
                          value={coachEmail}
                          onChange={(e) => setCoachEmail(e.target.value)}
                          placeholder="your.email@example.com"
                          required
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-2">Phone Number *</label>
                        <Input
                          value={coachPhone}
                          onChange={(e) => setCoachPhone(e.target.value)}
                          placeholder="+65 1234 5678"
                          required
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-2">Date of Birth *</label>
                        <Input
                          type="date"
                          value={coachBirthdate}
                          onChange={(e) => setCoachBirthdate(e.target.value)}
                          required
                        />
                      </div>
                      
                      <div className="border border-gray-300 rounded-lg p-4">
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={nrocRegistered}
                            onChange={(e) => setNrocRegistered(e.target.checked)}
                            className="w-4 h-4"
                          />
                          <span className="text-sm font-medium">NROC Registered</span>
                        </label>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-2">Coaching Certifications</label>
                        <select
                          multiple
                          value={coachingCertifications}
                          onChange={(e) => {
                            const selected = Array.from(e.target.selectedOptions, option => option.value);
                            setCoachingCertifications(selected);
                          }}
                          className="w-full p-2 border rounded min-h-[100px]"
                        >
                          <option value="UEFA A License">UEFA A License</option>
                          <option value="UEFA B License">UEFA B License</option>
                          <option value="UEFA C License">UEFA C License</option>
                          <option value="AFC A License">AFC A License</option>
                          <option value="AFC B License">AFC B License</option>
                          <option value="AFC C License">AFC C License</option>
                          <option value="FAS Grassroots">FAS Grassroots</option>
                          <option value="FAS Youth">FAS Youth</option>
                          <option value="None">None</option>
                          <option value="Other">Other</option>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">Hold Ctrl (Windows) or Cmd (Mac) to select multiple</p>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-2">Currently Coaching</label>
                        <Textarea
                          value={currentlyCoaching.join('\n')}
                          onChange={(e) => setCurrentlyCoaching(e.target.value.split('\n').filter(line => line.trim()))}
                          placeholder="Enter team/club names (one per line)&#10;Example:&#10;SCC U12&#10;ABC Football Club&#10;School Team"
                          rows={4}
                        />
                        <p className="text-xs text-gray-500 mt-1">Enter one team/club per line. Leave blank if not currently coaching.</p>
                      </div>
                      
                      <Button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-yellow-500/100 hover:bg-yellow-600 text-black font-bold"
                      >
                        {loading ? "Submitting..." : "Submit Application"}
                      </Button>
                      
                      <p className="text-xs text-gray-500 text-center">
                        We will review your application and contact you within 3-5 business days.
                      </p>
                    </form>
                  </div>
                )}
                
                {/* Step 12: Academy Parent Registration Form */}
                {registrationStep === 12 && userType === 'parent' && (
                  <div className="space-y-6">
                    <button
                      type="button"
                      onClick={() => setRegistrationStep(10)}
                      className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
                    >
                      ← Back
                    </button>
                    
                    <div className="text-center mb-4">
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">👪 Academy Parent Registration</h3>
                      <p className="text-gray-600">Register as an Academy Parent and enroll your children</p>
                    </div>
                    
                    <div className="bg-blue-500/10 border-l-4 border-blue-400 p-4 mb-4">
                      <p className="text-sm text-blue-300">
                        <strong>Note:</strong> Your registration will be reviewed by an admin. Once approved, you'll receive login credentials for both you and your children.
                      </p>
                    </div>
                    
                    <form onSubmit={async (e) => {
                      e.preventDefault();
                      if (!name || !email || !phone || !password || !parentAddress) {
                        toast.error("Please fill in all required parent fields");
                        return;
                      }
                      
                      // Validate children
                      for (let i = 0; i < children.length; i++) {
                        const child = children[i];
                        if (!child.given_name || !child.last_name || !child.birthdate || !child.gender || !child.emergency_contact || !child.emergency_relationship) {
                          toast.error(`Please fill in all required fields for child ${i + 1} (given name, last name, birthdate, gender, emergency contact)`);
                          return;
                        }
                      }
                      
                      setLoading(true);
                      try {
                        await axios.post(`${API}/academy/register-parent`, {
                          parent_given_name: givenName,
                          parent_last_name: lastName,
                          parent_name: name,  // Auto-generated for display
                          parent_email: email,
                          parent_phone: phone,
                          parent_password: password,
                          residential_address: parentAddress,
                          is_scc_member: isSccMember,
                          membership_number: membershipNumber || null,
                          children: children
                        });
                        
                        toast.success("Registration submitted! Admin will review and approve your registration. You'll receive an email with login details.");
                        resetForm();
                        setShowAuth(false);
                      } catch (error) {
                        console.error("Academy registration error:", error);
                        toast.error(error.response?.data?.detail || "Failed to submit registration");
                      } finally {
                        setLoading(false);
                      }
                    }} className="space-y-6">
                      
                      {/* Parent Information */}
                      <div className="bg-slate-800/50 p-4 rounded-lg border border-gray-200">
                        <h4 className="font-semibold text-gray-900 mb-4">Parent Information</h4>
                        
                        <div className="space-y-4">
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-sm font-medium mb-2">Given Name *</label>
                              <Input
                                value={givenName}
                                onChange={(e) => {
                                  setGivenName(e.target.value);
                                  setName(`${e.target.value} ${lastName}`.trim());
                                }}
                                placeholder="Johnny"
                                required
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium mb-2">Last Name *</label>
                              <Input
                                value={lastName}
                                onChange={(e) => {
                                  setLastName(e.target.value);
                                  setName(`${givenName} ${e.target.value}`.trim());
                                }}
                                placeholder="Tan"
                                required
                              />
                            </div>
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium mb-2">Email Address *</label>
                            <Input
                              type="email"
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                              placeholder="your.email@example.com"
                              required
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium mb-2">Phone Number *</label>
                            <Input
                              value={phone}
                              onChange={(e) => setPhone(e.target.value)}
                              placeholder="+65 1234 5678"
                              required
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium mb-2">Password *</label>
                            <Input
                              type="password"
                              value={password}
                              onChange={(e) => setPassword(e.target.value)}
                              placeholder="Create a strong password"
                              required
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium mb-2">Residential Address *</label>
                            <Input
                              value={parentAddress}
                              onChange={(e) => setParentAddress(e.target.value)}
                              placeholder="Your home address"
                              required
                            />
                          </div>
                          
                          <div>
                            <label className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={isSccMember}
                                onChange={(e) => setIsSccMember(e.target.checked)}
                                className="rounded"
                              />
                              <span className="text-sm">I am an SCC Member</span>
                            </label>
                          </div>
                          
                          {isSccMember && (
                            <div>
                              <label className="block text-sm font-medium mb-2">SCC Membership Number</label>
                              <Input
                                value={membershipNumber}
                                onChange={(e) => setMembershipNumber(e.target.value)}
                                placeholder="Your SCC membership card number"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Children Information */}
                      <div className="bg-blue-500/10 p-4 rounded-lg border border-blue-500/30">
                        <div className="flex justify-between items-center mb-4">
                          <h4 className="font-semibold text-gray-900">Children Information</h4>
                          <Button
                            type="button"
                            onClick={() => setChildren([...children, {
                              given_name: "",
                              last_name: "",
                              birthdate: "",
                              gender: "",
                              email: "",
                              emergency_contact: "",
                              emergency_relationship: "",
                              medical_info: ""
                            }])}
                            variant="outline"
                            size="sm"
                          >
                            + Add Another Child
                          </Button>
                        </div>
                        
                        {children.map((child, index) => (
                          <div key={index} className="mb-6 pb-6 border-b border-blue-500/30 last:border-0">
                            <div className="flex justify-between items-center mb-3">
                              <h5 className="font-medium text-gray-900">Child {index + 1}</h5>
                              <div className="flex items-center gap-3">
                                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        const newChildren = [...children];
                                        newChildren[index] = {
                                          ...newChildren[index],
                                          last_name: lastName,
                                          emergency_contact: phone,
                                          emergency_relationship: "Parent"
                                        };
                                        setChildren(newChildren);
                                      }
                                    }}
                                    className="rounded"
                                  />
                                  <span>Copy parent info</span>
                                </label>
                                {children.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => setChildren(children.filter((_, i) => i !== index))}
                                    className="text-red-600 text-sm hover:text-red-400"
                                  >
                                    Remove
                                  </button>
                                )}
                              </div>
                            </div>
                            
                            <div className="space-y-3">
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-sm font-medium mb-1">Given Name *</label>
                                  <Input
                                    value={child.given_name}
                                    onChange={(e) => {
                                      const newChildren = [...children];
                                      newChildren[index].given_name = e.target.value;
                                      setChildren(newChildren);
                                    }}
                                    placeholder="Johnny"
                                    required
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium mb-1">Last Name *</label>
                                  <Input
                                    value={child.last_name}
                                    onChange={(e) => {
                                      const newChildren = [...children];
                                      newChildren[index].last_name = e.target.value;
                                      setChildren(newChildren);
                                    }}
                                    placeholder="Tan"
                                    required
                                  />
                                </div>
                              </div>
                              
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-sm font-medium mb-1">Date of Birth *</label>
                                  <Input
                                    type="date"
                                    value={child.birthdate}
                                    onChange={(e) => {
                                      const newChildren = [...children];
                                      newChildren[index].birthdate = e.target.value;
                                      setChildren(newChildren);
                                    }}
                                    required
                                  />
                                </div>
                                
                                <div>
                                  <label className="block text-sm font-medium mb-1">Gender *</label>
                                  <select
                                    value={child.gender}
                                    onChange={(e) => {
                                      const newChildren = [...children];
                                      newChildren[index].gender = e.target.value;
                                      setChildren(newChildren);
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                                    required
                                  >
                                    <option value="">Select</option>
                                    <option value="male">Male</option>
                                    <option value="female">Female</option>
                                  </select>
                                </div>
                              </div>
                              
                              <div>
                                <label className="block text-sm font-medium mb-1">Child's Email (optional)</label>
                                <Input
                                  type="email"
                                  value={child.email}
                                  onChange={(e) => {
                                    const newChildren = [...children];
                                    newChildren[index].email = e.target.value;
                                    setChildren(newChildren);
                                  }}
                                  placeholder="Optional email for older children"
                                />
                              </div>
                              
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-sm font-medium mb-1">Emergency Contact *</label>
                                  <Input
                                    value={child.emergency_contact}
                                    onChange={(e) => {
                                      const newChildren = [...children];
                                      newChildren[index].emergency_contact = e.target.value;
                                      setChildren(newChildren);
                                    }}
                                    placeholder="Phone number"
                                    required
                                  />
                                </div>
                                
                                <div>
                                  <label className="block text-sm font-medium mb-1">Relationship *</label>
                                  <Input
                                    value={child.emergency_relationship}
                                    onChange={(e) => {
                                      const newChildren = [...children];
                                      newChildren[index].emergency_relationship = e.target.value;
                                      setChildren(newChildren);
                                    }}
                                    placeholder="e.g., Parent, Guardian"
                                    required
                                  />
                                </div>
                              </div>
                              
                              <div>
                                <label className="block text-sm font-medium mb-1">Medical Information (optional)</label>
                                <Input
                                  value={child.medical_info}
                                  onChange={(e) => {
                                    const newChildren = [...children];
                                    newChildren[index].medical_info = e.target.value;
                                    setChildren(newChildren);
                                  }}
                                  placeholder="Allergies, conditions, medications..."
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      <div className="bg-slate-800/50 p-4 rounded-lg border border-gray-200">
                        <h4 className="font-medium text-gray-900 mb-2">What happens next?</h4>
                        <ul className="text-sm text-gray-600 space-y-1">
                          <li>✓ Admin will review your registration</li>
                          <li>✓ Once approved, accounts will be created for you and your children</li>
                          <li>✓ You'll receive login credentials via email</li>
                          <li>✓ Admin will assign your children to appropriate age-group teams</li>
                        </ul>
                      </div>
                      
                      <Button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold"
                      >
                        {loading ? "Submitting..." : "Register for Academy"}
                      </Button>
                      
                      <p className="text-xs text-gray-500 text-center">
                        Registration typically takes 2-3 business days for approval.
                      </p>
                    </form>
                  </div>
                )}
              </TabsContent>
            </Tabs>

            <Button
              onClick={() => setShowAuth(false)}
              variant="ghost"
              className="w-full mt-4"
            >
              Close
            </Button>
          </Card>
        </div>
      )}

      {/* Features Grid */}
      <section className="container mx-auto px-4 py-16">
        <h2 className="font-display text-3xl md:text-4xl font-bold text-center mb-2" style={{color: 'var(--text-primary)'}}>Football And More</h2>
        <p className="text-center text-lg font-semibold mb-4" style={{color: 'var(--accent-primary)'}}>Membership has privileges</p>
        <p className="text-center mb-12 max-w-2xl mx-auto" style={{color: 'var(--text-muted)'}}>
          {brandingMode === 'scc' 
            ? "More than just a club. SCC Football offers members a complete football journey from youth to senior football."
            : "More than just a club. The football section offers members a complete football journey from youth to senior football."
          }
        </p>
        
        <div className="grid md:grid-cols-3 gap-8">
          <div 
            className="relative"
            onMouseEnter={() => setHoveredCard('teams')}
            onMouseLeave={() => setHoveredCard(null)}
          >
            <Card className="force-dark p-8 card-hover bg-gray-900 border border-yellow-500/30 shadow-lg transition-all hover:border-yellow-500/60" data-testid="feature-teams">
              <div className="w-14 h-14 rounded-full bg-yellow-500/10 flex items-center justify-center mb-4">
                <Users className="w-7 h-7 text-yellow-500" />
              </div>
              <h3 className="font-display text-xl font-bold text-white mb-2">Join Teams</h3>
              <p className="text-gray-400">Connect with your teammates and stay updated</p>
              {stats.teamsCount > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-700">
                  <span className="text-sm font-semibold text-yellow-500">🏆 {stats.teamsCount} Active Teams</span>
                </div>
              )}
            </Card>
            {hoveredCard === 'teams' && stats.teamsCount > 0 && (
              <div className="absolute z-10 top-full left-0 right-0 mt-2 p-4 bg-gray-900 border border-yellow-500/50 rounded-lg shadow-xl">
                <p className="text-sm text-gray-300 font-medium">
                  Join one of our {stats.teamsCount} active teams competing in various leagues and social games!
                </p>
              </div>
            )}
          </div>

          <div 
            className="relative"
            onMouseEnter={() => setHoveredCard('games')}
            onMouseLeave={() => setHoveredCard(null)}
          >
            <Card className="force-dark p-8 card-hover bg-gray-900 border border-yellow-500/30 shadow-lg transition-all hover:border-yellow-500/60" data-testid="feature-games">
              <div className="w-14 h-14 rounded-full bg-yellow-500/10 flex items-center justify-center mb-4">
                <Trophy className="w-7 h-7 text-yellow-500" />
              </div>
              <h3 className="font-display text-xl font-bold text-white mb-2">Game Schedules</h3>
              <p className="text-gray-400">View upcoming games and sign up instantly</p>
              {stats.upcomingGames > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-700">
                  <span className="text-sm font-semibold text-yellow-500">⚽ {stats.upcomingGames} Upcoming Games</span>
                </div>
              )}
            </Card>
            {hoveredCard === 'games' && stats.upcomingGames > 0 && (
              <div className="absolute z-10 top-full left-0 right-0 mt-2 p-4 bg-gray-900 border border-yellow-500/50 rounded-lg shadow-xl">
                <p className="text-sm text-gray-300 font-medium mb-2">
                  {stats.upcomingGames} games scheduled! Sign up now to secure your spot.
                </p>
              </div>
            )}
          </div>

          <div 
            className="relative"
            onMouseEnter={() => setHoveredCard('reports')}
            onMouseLeave={() => setHoveredCard(null)}
          >
            <Card className="force-dark p-8 card-hover bg-gray-900 border border-yellow-500/30 shadow-lg transition-all hover:border-yellow-500/60" data-testid="feature-reports">
              <div className="w-14 h-14 rounded-full bg-yellow-500/10 flex items-center justify-center mb-4">
                <Calendar className="w-7 h-7 text-yellow-500" />
              </div>
              <h3 className="font-display text-xl font-bold text-white mb-2">Match Reports</h3>
              <p className="text-gray-400">Read exciting match reports and player highlights</p>
              {stats.recentReports.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-700">
                  <span className="text-sm font-semibold text-yellow-500">📝 {stats.recentReports.length} Recent Reports</span>
                </div>
              )}
            </Card>
            {hoveredCard === 'reports' && stats.recentReports.length > 0 && (
              <div className="absolute z-10 top-full left-0 right-0 mt-2 p-4 bg-gray-900 border border-yellow-500/50 rounded-lg shadow-xl max-h-64 overflow-y-auto">
                <p className="text-xs font-semibold text-gray-500 mb-2">LATEST REPORTS:</p>
                {stats.recentReports.map((report, idx) => (
                  <div key={idx} className="mb-2 pb-2 border-b last:border-b-0 border-gray-700">
                    <p className="text-sm font-bold text-white">{report.title}</p>
                    <p className="text-xs text-gray-400">
                      {report.team_name} • Score: {report.score_home}-{report.score_away}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div 
            className="relative"
            onMouseEnter={() => setHoveredCard('events')}
            onMouseLeave={() => setHoveredCard(null)}
          >
            <Card className="force-dark p-8 card-hover bg-gray-900 border border-yellow-500/30 shadow-lg transition-all hover:border-yellow-500/60" data-testid="feature-events">
              <div className="w-14 h-14 rounded-full bg-yellow-500/10 flex items-center justify-center mb-4">
                <Shield className="w-7 h-7 text-yellow-500" />
              </div>
              <h3 className="font-display text-xl font-bold text-white mb-2">Events Calendar</h3>
              <p className="text-gray-400">Never miss tournaments and social events</p>
              {stats.upcomingEvents > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-700">
                  <span className="text-sm font-semibold text-yellow-500">📅 {stats.upcomingEvents} Upcoming Events</span>
                </div>
              )}
            </Card>
            {hoveredCard === 'events' && stats.upcomingEvents > 0 && (
              <div className="absolute z-10 top-full left-0 right-0 mt-2 p-4 bg-gray-900 border border-yellow-500/50 rounded-lg shadow-xl">
                <p className="text-sm text-gray-300 font-medium">
                  {stats.upcomingEvents} exciting events coming up! Tournaments, social gatherings, and more.
                </p>
              </div>
            )}
          </div>

          <div 
            className="relative"
            onMouseEnter={() => setHoveredCard('discounts')}
            onMouseLeave={() => setHoveredCard(null)}
          >
            <Card className="force-dark p-8 card-hover bg-gray-900 border border-yellow-500/30 shadow-lg transition-all hover:border-yellow-500/60" data-testid="feature-discounts">
              <div className="w-14 h-14 rounded-full bg-yellow-500/10 flex items-center justify-center mb-4">
                <Tag className="w-7 h-7 text-yellow-500" />
              </div>
              <h3 className="font-display text-xl font-bold text-white mb-2">Member Discounts</h3>
              <p className="text-gray-400">Exclusive deals with partner merchants</p>
              {stats.activeDiscounts > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-700">
                  <span className="text-sm font-semibold text-yellow-500">🎁 {stats.activeDiscounts} Active Discounts</span>
                </div>
              )}
            </Card>
            {hoveredCard === 'discounts' && stats.activeDiscounts > 0 && (
              <div className="absolute z-10 top-full left-0 right-0 mt-2 p-4 bg-gray-900 border border-yellow-500/50 rounded-lg shadow-xl">
                <p className="text-sm text-gray-300 font-medium">
                  {stats.activeDiscounts} exclusive member discounts available from our partner merchants!
                </p>
              </div>
            )}
          </div>

          {/* Academy Card */}
          <div 
            className="relative"
            onMouseEnter={() => setHoveredCard('academy')}
            onMouseLeave={() => setHoveredCard(null)}
          >
            <Card className="force-dark p-8 card-hover bg-gray-900 border border-yellow-500/30 shadow-lg transition-all hover:border-yellow-500/60" data-testid="feature-academy">
              <div className="w-14 h-14 rounded-full bg-yellow-500/10 flex items-center justify-center mb-4">
                <GraduationCap className="w-7 h-7 text-yellow-500" />
              </div>
              <h3 className="font-display text-xl font-bold text-white mb-2">Youth Academy</h3>
              <p className="text-gray-400">Football training for children ages 4-16</p>
              <div className="mt-4 pt-4 border-t border-gray-700">
                {stats.academyPlayers > 0 ? (
                  <span className="text-sm font-semibold text-yellow-500">⚽ {stats.academyPlayers}+ Young Players</span>
                ) : (
                  <span className="text-sm font-semibold text-yellow-500">⚽ Ages 4-16 Welcome</span>
                )}
              </div>
            </Card>
            {hoveredCard === 'academy' && (
              <div className="absolute z-10 top-full left-0 right-0 mt-2 p-4 bg-gray-900 border border-yellow-500/50 rounded-lg shadow-xl">
                <p className="text-sm text-gray-300 font-medium mb-2">
                  {branding.clubName} Academy — quality coaching for young players!
                </p>
                <p className="text-xs text-gray-500">
                  Professional coaches • Multiple training sessions weekly • Development pathway to senior teams
                </p>
              </div>
            )}
          </div>

          <div 
            className="relative"
            onMouseEnter={() => setHoveredCard('membership')}
            onMouseLeave={() => setHoveredCard(null)}
          >
            <Card className="force-dark p-8 card-hover bg-gray-900 border border-yellow-500/30 shadow-lg transition-all hover:border-yellow-500/60" data-testid="feature-card">
              <div className="w-14 h-14 rounded-full bg-yellow-500/10 flex items-center justify-center mb-4">
                <Zap className="w-7 h-7 text-yellow-500" />
              </div>
              <h3 className="font-display text-xl font-bold text-white mb-2">Digital Membership</h3>
              <p className="text-gray-400">Access your digital membership card anytime</p>
              {stats.totalMembers > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-700">
                  <span className="text-sm font-semibold text-yellow-500">👥 {stats.totalMembers}+ Active Members</span>
                </div>
              )}
            </Card>
            {hoveredCard === 'membership' && stats.totalMembers > 0 && (
              <div className="absolute z-10 top-full left-0 right-0 mt-2 p-4 bg-gray-900 border border-yellow-500/50 rounded-lg shadow-xl">
                <p className="text-sm text-gray-300 font-medium mb-2">
                  Join our community of {stats.totalMembers}+ active members!
                </p>
                <p className="text-xs text-gray-500">
                  Digital membership card with QR code • Instant access • Always available on your device
                </p>
              </div>
            )}
          </div>
        </div>
        
        {/* Get Started Button */}
        <div className="text-center mt-12">
          <Button
            onClick={() => setShowAuth(true)}
            size="lg"
            className="bg-yellow-500/100 hover:bg-yellow-400 text-black font-bold px-10 py-6 text-lg rounded-full shadow-lg hover:shadow-yellow-500/25 transition-all duration-300"
          >
            Get Started
          </Button>
        </div>
      </section>

      {/* Merchant Partnership CTA */}
      <div className="container mx-auto px-4 py-16">
        <Card className="p-12 text-center bg-gradient-to-r from-gray-900 to-gray-800 text-white shadow-2xl border border-yellow-500/30">
          <Store className="w-16 h-16 mx-auto mb-4 text-yellow-500" />
          <h2 className="font-display text-3xl font-bold mb-4">Become a Partner Merchant</h2>
          <p className="text-gray-400 mb-6 max-w-2xl mx-auto">
            Join our network of trusted merchants! Offer exclusive deals to {branding.clubName} members and grow your business with our engaged community of over 500+ members.
          </p>
          <a href="/apply-merchant">
            <Button
              size="lg"
              className="bg-yellow-500/100 hover:bg-yellow-400 text-black font-bold text-lg px-8 py-6 rounded-full"
            >
              Apply Now
              <Store className="ml-2 w-5 h-5" />
            </Button>
          </a>
          <p className="text-sm text-gray-500 mt-4">No account needed • One-step application • Fast approval</p>
          
          {/* Get Started Button */}
          <div className="mt-8 pt-6 border-t border-gray-700">
            <p className="text-gray-500 text-sm mb-3">Ready to join as a member?</p>
            <Button
              onClick={() => setShowAuth(true)}
              size="lg"
              variant="outline"
              className="border-yellow-500 text-yellow-500 hover:bg-yellow-500/100 hover:text-black font-bold px-8 py-4 rounded-full transition-all"
            >
              Get Started
            </Button>
          </div>
        </Card>
      </div>

      {/* Membership Enquiry Section */}
      <div className="container mx-auto px-4 py-16" data-testid="membership-enquiry-section">
        <Card className="p-12 bg-gray-900/50 border border-yellow-500/30 shadow-2xl">
          <h2 className="font-display text-4xl font-bold text-white mb-4 text-center">Interested in Membership?</h2>
          <p className="text-gray-400 mb-8 text-center max-w-2xl mx-auto">
            Need more information? Send us your enquiry and our membership team will get back to you with more information about membership benefits, fees, and how to join.
          </p>
          
          <form onSubmit={async (e) => {
            e.preventDefault();
            const formData = new FormData(e.target);
            const data = {
              name: formData.get('enquiry_name'),
              email: formData.get('enquiry_email'),
              phone: formData.get('enquiry_phone'),
              message: formData.get('enquiry_message')
            };
            
            try {
              setLoading(true);
              await axios.post(`${API}/contact/membership-enquiry`, data);
              toast.success("Enquiry sent! We'll contact you soon.");
              e.target.reset();
            } catch (error) {
              console.error("Error sending enquiry:", error);
              toast.error("Failed to send enquiry. Please try again.");
            } finally {
              setLoading(false);
            }
          }} className="max-w-2xl mx-auto space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <Input
                name="enquiry_name"
                placeholder="Your Name *"
                required
                className="bg-gray-800 border-gray-700 text-white placeholder:text-gray-500 focus:border-yellow-500"
              />
              <Input
                name="enquiry_email"
                type="email"
                placeholder="Email Address *"
                required
                className="bg-gray-800 border-gray-700 text-white placeholder:text-gray-500 focus:border-yellow-500"
              />
            </div>
            
            <Input
              name="enquiry_phone"
              placeholder="Phone Number (optional)"
              className="bg-gray-800 border-gray-700 text-white placeholder:text-gray-500 focus:border-yellow-500"
            />
            
            <Textarea
              name="enquiry_message"
              placeholder={`Tell us about your interest in ${branding.clubName} membership...`}
              rows={4}
              className="bg-gray-800 border-gray-700 text-white placeholder:text-gray-500 focus:border-yellow-500"
            />
            
            <Button
              type="submit"
              size="lg"
              className="w-full bg-yellow-500/100 hover:bg-yellow-400 text-black font-bold rounded-full"
              disabled={loading}
            >
              {loading ? "Sending..." : "Send Enquiry"}
            </Button>
          </form>
          
          {/* Get Started Button */}
          <div className="mt-8 pt-6 border-t border-gray-700 text-center">
            <p className="text-gray-500 text-sm mb-3">Already know what you want?</p>
            <Button
              onClick={() => setShowAuth(true)}
              size="lg"
              variant="outline"
              className="border-yellow-500 text-yellow-500 hover:bg-yellow-500/100 hover:text-black font-bold px-8 py-4 rounded-full transition-all"
            >
              Get Started Now
            </Button>
          </div>
        </Card>
      </div>

      {/* Website Link Section - SCC only */}
      {branding.isSccBranding && (
        <div className="container mx-auto px-4 py-16">
          <Card className="p-12 text-center bg-gray-900/50 border border-yellow-500/30 shadow-2xl">
            <h2 className="font-display text-3xl font-bold text-white mb-4">Visit Our Website</h2>
            <p className="text-gray-400 mb-6">Learn more about SCC Football</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://www.sccfootball.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block"
                data-testid="website-link"
              >
                <Button
                  size="lg"
                  variant="outline"
                  className="border-gray-600 text-gray-300 hover:bg-gray-800 font-bold rounded-full"
                >
                  www.sccfootball.com
                </Button>
              </a>
              <Button
                onClick={() => setShowAuth(true)}
                size="lg"
                className="bg-yellow-500/100 hover:bg-yellow-400 text-black font-bold rounded-full"
              >
                Get Started
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Footer */}
      <footer className="force-dark bg-black border-t border-gray-800 text-white py-12">
        <div className="container mx-auto px-4 text-center">
          {branding.isSccBranding ? (
            <>
              <img 
                src="/branding/football-and-more-white.png"
                alt="Football And More Logo"
                className="h-16 w-auto mx-auto mb-4 opacity-60"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              <p className="text-gray-500 text-sm">Football And More</p>
              <p className="text-gray-600 text-xs mt-2">© 2026 Football And More. All rights reserved.</p>
            </>
          ) : (
            <>
              <img 
                src="/branding/football-and-more-white.png"
                alt="Football And More Logo"
                className="h-16 w-auto mx-auto mb-4 opacity-60"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <p className="text-gray-500 text-sm">{branding.clubName}</p>
              <p className="text-gray-600 text-xs mt-2">© 2026 {branding.clubName}. All rights reserved.</p>
            </>
          )}
        </div>
      </footer>

      {/* Membership-validation prompt (Feb 2026) */}
      {mnPromptOpen && (
        <div
          className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
          data-testid="membership-validation-modal"
          onClick={() => !loading && setMnPromptOpen(false)}
        >
          <Card
            className="bg-white text-gray-900 w-full max-w-lg p-6 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold">Membership couldn't be validated</h3>
            <p className="text-sm text-gray-700">{mnPromptMsg}</p>
            <p className="text-xs text-gray-500">
              If you're unsure of your number, please contact Singapore Cricket Club. Otherwise pick one of the options below.
            </p>
            <div className="flex flex-col sm:flex-row gap-2 pt-2">
              <Button
                onClick={handleProceedAnyway}
                disabled={loading}
                className="bg-yellow-500 hover:bg-yellow-400 text-black flex-1"
                data-testid="mn-proceed-anyway-btn"
              >
                Proceed anyway — admin will verify
              </Button>
              <Button
                onClick={handleSwitchToNonMember}
                disabled={loading}
                variant="outline"
                className="flex-1"
                data-testid="mn-switch-nonmember-btn"
              >
                Continue as non-member
              </Button>
            </div>
            <button
              onClick={() => !loading && setMnPromptOpen(false)}
              disabled={loading}
              className="text-xs text-gray-500 hover:text-gray-700 underline"
              data-testid="mn-cancel-btn"
            >
              Cancel and edit my membership number
            </button>
          </Card>
        </div>
      )}
    </div>
  );
}
